import { NetworkStatus } from '@apollo/client';
import { useCallback, useEffect } from 'react';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import type { Asset } from '@/types/GraphqlSchema';
import { VALUATIONS_V2_PATH } from '@/utils/constants';
import { useValuationsStore, valuationsStoreBase } from '../store/valuationsStore';
import { useAssets } from './useAssets';
import { useQueryVariables } from './useQueryVariables';

type RouteParams = {
  countryIsoCode?: string;
  legacyId?: string;
};

const useSelectedAssetRouteSync = () => {
  const history = useHistory();
  const location = useLocation();
  const { countryIsoCode, legacyId: assetId } = useParams<RouteParams>();
  const setSelectedAsset = useValuationsStore.use.setSelectedAsset();

  const selectedAsset = useValuationsStore.use.selectedAsset();

  useEffect(() => {
    if (countryIsoCode && assetId) {
      const currentSelectedAsset = valuationsStoreBase.getState().selectedAsset;
      const currentSelectedAssetId = currentSelectedAsset?.id ?? null;

      if (!currentSelectedAssetId) {
        setSelectedAsset({
          id: assetId,
          country: {
            isoCode: countryIsoCode,
          },
        } as Asset);
      }
      return;
    }
  }, [countryIsoCode, assetId, setSelectedAsset]);

  useEffect(() => {
    if (!selectedAsset) {
      return;
    }

    const assetIsoCode = selectedAsset.country?.isoCode;

    if (!assetIsoCode || !selectedAsset.id) {
      return;
    }

    const targetPathname = `${VALUATIONS_V2_PATH}/${assetIsoCode}/${selectedAsset.id}`;

    if (location.pathname === targetPathname) {
      return;
    }

    history.replace({
      pathname: targetPathname,
      search: location.search,
      hash: location.hash,
    });
  }, [selectedAsset, history, location.pathname, location.search, location.hash]);
};

export const useGetAssetsList = () => {
  useSelectedAssetRouteSync();

  const setSortedAssets = useValuationsStore.use.setSortedAssets();
  const setSelectedAsset = useValuationsStore.use.setSelectedAsset();
  const setTotalCount = useValuationsStore.use.setTotalCount();
  const setLoadingAssets = useValuationsStore.use.setLoadingAssets();
  const setLoadingMoreAssets = useValuationsStore.use.setLoadingMoreAssets();
  const setAssetsPageInfo = useValuationsStore.use.setAssetsPageInfo();
  const watchListView = useValuationsStore.use.watchListView();
  const variables = useQueryVariables();

  const {
    data: assetsData,
    loading,
    fetchMore,
    networkStatus,
  } = useAssets(variables, { skip: watchListView });

  useEffect(() => {
    setLoadingAssets(loading && networkStatus !== NetworkStatus.fetchMore);
  }, [loading, networkStatus, setLoadingAssets]);

  useEffect(() => {
    setLoadingMoreAssets(networkStatus === NetworkStatus.fetchMore);
  }, [networkStatus, setLoadingMoreAssets]);

  useEffect(() => {
    const connection = assetsData?.assetsConnection;
    if (!connection) return;

    const edges = connection.edges ?? [];
    const allAssets = edges
      .map(edge => edge?.node ?? null)
      .filter((asset): asset is Asset => asset !== null);

    setAssetsPageInfo({
      endCursor: connection?.pageInfo?.endCursor || null,
      hasNextPage: connection?.pageInfo?.hasNextPage,
    });

    setTotalCount(connection.totalCount);
    setSortedAssets(allAssets);

    const currentSelectedAsset = valuationsStoreBase.getState().selectedAsset;
    const currentSelectedAssetId = currentSelectedAsset?.id ?? null;
    if (!currentSelectedAssetId) {
      setSelectedAsset(allAssets[0] ?? null);
    }
  }, [assetsData, setAssetsPageInfo, setSortedAssets, setTotalCount, setSelectedAsset]);

  const loadMoreAssets = useCallback(async () => {
    const {
      assetsPageInfo,
      loadingAssets: isLoadingAssets,
      loadingMoreAssets,
    } = valuationsStoreBase.getState();

    if (isLoadingAssets || loadingMoreAssets) return;

    if (!assetsPageInfo.hasNextPage || !assetsPageInfo.endCursor) return;

    setLoadingMoreAssets(true);

    try {
      await fetchMore({
        variables: { after: assetsPageInfo.endCursor },
        updateQuery: (previousResult, { fetchMoreResult }) => {
          const previousConnection = previousResult?.assetsConnection;
          const nextConnection = fetchMoreResult?.assetsConnection;

          if (!nextConnection) {
            return previousResult;
          }

          if (!previousConnection) {
            return fetchMoreResult;
          }

          const previousEdges = previousConnection.edges ?? [];
          const nextEdges = nextConnection.edges ?? [];

          const seenIds = new Set(
            previousEdges
              .map(edge => edge?.node?.id ?? null)
              .filter((id): id is string => id !== null),
          );

          const mergedEdges = [
            ...previousEdges,
            ...nextEdges.filter(edge => {
              const nodeId = edge?.node?.id;
              if (!nodeId) {
                return true;
              }
              if (seenIds.has(nodeId)) {
                return false;
              }
              seenIds.add(nodeId);
              return true;
            }),
          ];

          return {
            assetsConnection: {
              ...nextConnection,
              edges: mergedEdges,
            },
          };
        },
      });
    } catch (error) {
      console.error('Failed to load more assets', error);
    } finally {
      setLoadingMoreAssets(false);
    }
  }, [fetchMore, setLoadingMoreAssets]);

  return { loadMoreAssets };
};
